import React, { useMemo, useState } from 'react';
import { Col, Container, Row, Button, Form } from 'react-bootstrap';
import { Modal, Select, Space, message } from 'antd';
import { GoodsItem } from './GoodsItems';
import { GoodsItemHeader } from './GoodsItemHeader';
import { useGoods } from './useGoods';
import { Paginator, getPageNumers } from '../shared/Paginator';

const Goods = () => {
  const {
    goods,
    createGoods,
    showNew,
    setShowNew,
    deleteGoods,
    restoreGoods,
    updateGoods,
    brands,
    productCategories,
    availableTags,
    refreshTags,
    selectedTagIds,
    setSelectedTagIds,
    page,
    totalPage,
    total,
    filter,
    setFilter,
    handlePageChange,
    showDeleted,
    setShowDeleted,
    isCombo,
    setIsCombo,
    fbs,
    setFbs,
    mpAuths,
    linkedAuthIds,
    setLinkedAuthIds,
    unlinkedAuthIds,
    setUnlinkedAuthIds,
    brand,
    setBrand,
    category,
    setCategory,
    hasActiveFilters,
    hasFbsBulkScope,
    enableFbsByFilter,
    pending,
  } = useGoods();
  const [enablingFbs, setEnablingFbs] = useState(false);

  const paginatorItems = useMemo(() => {
    return getPageNumers(totalPage, page);
  }, [totalPage, page]);

  const tagFilterOptions = useMemo(
    () =>
      [...availableTags]
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' })
        )
        .map((t) => ({ value: t.id, label: t.name })),
    [availableTags]
  );

  const brandOptions = useMemo(
    () =>
      [
        { value: '__empty__', label: 'без бренда' },
        ...[...brands]
          .sort((a, b) =>
            a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' })
          )
          .map((b) => ({ value: b.name, label: b.name })),
      ],
    [brands]
  );

  const categoryOptions = useMemo(
    () =>
      [
        { value: '__empty__', label: 'без категории' },
        ...[...productCategories]
          .sort((a, b) =>
            a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' })
          )
          .map((c) => ({ value: c.name, label: c.name })),
      ],
    [productCategories]
  );

  const mpAuthOptions = useMemo(() => {
    const mpShort = (m: string) => {
      const v = m.toLowerCase();
      if (v === 'wildberries' || v === 'wb') return 'WB';
      if (v === 'ozon') return 'Ozon';
      return m;
    };
    return mpAuths.map((a) => ({
      value: a.id,
      label: `${mpShort(a.marketplace)} #${a.id}`,
    }));
  }, [mpAuths]);

  return (
    <Container fluid style={{ maxWidth: 1400 }}>
      <Row>
        <Col>
          <Button
            style={{ margin: '16px 0' }}
            onClick={() => setShowNew(!showNew)}
          >
            {!showNew ? 'Создать товар' : 'Скрыть товар'}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          {showNew && (
            <GoodsItem
              onCreate={createGoods}
              isNew
              brands={brands}
              productCategories={productCategories}
              availableTags={availableTags}
              onTagsCatalogChange={refreshTags}
            />
          )}
        </Col>
      </Row>
      <Row style={{ marginTop: 8, marginBottom: 8, rowGap: 8 }}>
        <Col md={4}>
          <Form.Control
            placeholder="Поиск: артикул, название, баркод МП, alias, категория..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
        <Col md={4}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Фильтр по тегам"
            style={{ width: '100%' }}
            value={selectedTagIds}
            options={tagFilterOptions}
            optionFilterProp="label"
            onChange={(ids: number[]) => setSelectedTagIds(ids)}
          />
        </Col>
        <Col md={4} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Form.Check
            type="checkbox"
            label="Показать удаленные"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          {total > 0 && (
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>
                Найдено: {total}{' '}
                {total === 1 ? 'товар' : total < 5 ? 'товара' : 'товаров'}
              </span>
              <Button
                size="sm"
                variant="success"
                disabled={pending || enablingFbs || !hasFbsBulkScope}
                title={
                  showDeleted
                    ? 'Нельзя включить FBS у удалённых товаров'
                    : hasFbsBulkScope
                    ? `Включить FBS для ${total} товаров по текущему фильтру`
                    : 'Сначала отфильтруйте список (тег, поиск, бренд, категория…)'
                }
                onClick={() => {
                  Modal.confirm({
                    title: 'Включить FBS?',
                    content: `Включить FBS для ${total} товаров по текущему фильтру? Уже включённые не изменятся. Удалённые не затрагиваются.`,
                    okText: 'Включить FBS',
                    cancelText: 'Отмена',
                    onOk: async () => {
                      setEnablingFbs(true);
                      try {
                        const res = await enableFbsByFilter();
                        message.success(
                          `FBS: включено ${res.enabled}, уже было ${res.alreadyOn} из ${res.matched}`
                        );
                      } catch (e: any) {
                        message.error(e?.message || 'Не удалось включить FBS');
                        throw e;
                      } finally {
                        setEnablingFbs(false);
                      }
                    },
                  });
                }}
              >
                {enablingFbs ? 'Включаем…' : 'Включить FBS'}
              </Button>
            </span>
          )}
        </Col>
      </Row>
      <Row style={{ marginBottom: 12, rowGap: 8 }}>
        <Col>
          <Space wrap size={[8, 8]} style={{ width: '100%' }}>
            <Select
              allowClear
              placeholder="Комбо"
              style={{ width: 140 }}
              value={isCombo || undefined}
              options={[
                { value: 'true', label: 'комбо' },
                { value: 'false', label: 'не комбо' },
              ]}
              onChange={(v) => setIsCombo((v as '' | 'true' | 'false') || '')}
            />
            <Select
              allowClear
              placeholder="FBS"
              style={{ width: 140 }}
              value={fbs || undefined}
              options={[
                { value: 'true', label: 'FBS вкл.' },
                { value: 'false', label: 'FBS выкл.' },
              ]}
              onChange={(v) => setFbs((v as '' | 'true' | 'false') || '')}
            />
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Есть связь с…"
              style={{ minWidth: 200 }}
              value={linkedAuthIds}
              options={mpAuthOptions}
              optionFilterProp="label"
              onChange={(ids: number[]) => setLinkedAuthIds(ids)}
            />
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Нет связи с…"
              style={{ minWidth: 200 }}
              value={unlinkedAuthIds}
              options={mpAuthOptions}
              optionFilterProp="label"
              onChange={(ids: number[]) => setUnlinkedAuthIds(ids)}
            />
            <Select
              allowClear
              showSearch
              placeholder="Бренд"
              style={{ width: 180 }}
              value={brand || undefined}
              options={brandOptions}
              optionFilterProp="label"
              onChange={(v) => setBrand(v || '')}
            />
            <Select
              allowClear
              showSearch
              placeholder="Категория"
              style={{ width: 200 }}
              value={category || undefined}
              options={categoryOptions}
              optionFilterProp="label"
              onChange={(v) => setCategory(v || '')}
            />
          </Space>
        </Col>
      </Row>
      {totalPage > 1 && (
        <Row style={{ marginBottom: 16 }}>
          <Col>
            <Paginator
              currentPageNumber={page}
              setCurrentPageNumber={handlePageChange}
              items={paginatorItems}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          {!pending && goods.length > 0 && <GoodsItemHeader />}
          {pending && <div>Загрузка...</div>}
          {!pending && goods.length === 0 && !hasActiveFilters && (
            <div>Нет созданных товаров</div>
          )}
          {!pending && goods.length === 0 && hasActiveFilters && (
            <div>Товары не найдены</div>
          )}
          {!pending &&
            goods.map((el) => (
              <GoodsItem
                key={el.article}
                data={el}
                article={el.article}
                onUpdate={updateGoods}
                onDelete={deleteGoods}
                onRestore={restoreGoods}
                brands={brands}
                productCategories={productCategories}
                availableTags={availableTags}
                onTagsCatalogChange={refreshTags}
              />
            ))}
        </Col>
      </Row>
      {totalPage > 0 && (
        <Row style={{ marginTop: 16 }}>
          <Col>
            <Paginator
              currentPageNumber={page}
              setCurrentPageNumber={handlePageChange}
              items={paginatorItems}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default Goods;
